import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';

const CartPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, total } = useCart();

  const placeholderImage = 'https://images.unsplash.com/photo-1592868378269-2c502b2730ff?w=200&h=200&fit=crop';

  if (items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center py-16" data-testid="empty-cart">
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
            <ShoppingCart className="h-10 w-10 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold font-serif mb-4">{t('emptyCart')}</h1>
          <p className="text-muted-foreground mb-8">
            Non hai ancora aggiunto prodotti al carrello.
          </p>
          <Link to="/products">
            <Button className="btn-primary">
              {t('continueShopping')}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 md:py-16" data-testid="cart-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <h1 className="text-3xl md:text-4xl font-bold font-serif mb-8">{t('yourCart')}</h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {items.map((item) => (
              <div
                key={item.product_id}
                className="bg-white rounded-2xl border border-border p-4 md:p-6 flex gap-4 md:gap-6"
                data-testid={`cart-item-${item.product_id}`}
              >
                {/* Image */}
                <Link to={`/products/${item.product_id}`} className="shrink-0">
                  <div className="w-24 h-24 md:w-32 md:h-32 rounded-xl overflow-hidden bg-muted">
                    <img
                      src={item.image || placeholderImage}
                      alt={item.title}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = placeholderImage; }}
                    />
                  </div>
                </Link>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <Link to={`/products/${item.product_id}`}>
                    <h3 className="font-medium text-foreground hover:text-primary transition-colors line-clamp-2">
                      {item.title}
                    </h3>
                  </Link>
                  <p className="text-lg font-bold text-primary mt-1">
                    €{item.price?.toFixed(2)}
                  </p>

                  <div className="flex items-center justify-between mt-4">
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-2 bg-muted rounded-full p-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        data-testid={`decrease-qty-${item.product_id}`}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="w-8 text-center font-medium">{item.quantity}</span>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 rounded-full"
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        data-testid={`increase-qty-${item.product_id}`}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Remove Button */}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => removeItem(item.product_id)}
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {t('remove')}
                    </Button>
                  </div>
                </div>

                {/* Item Total - Desktop */}
                <div className="hidden md:flex flex-col items-end justify-between">
                  <span className="text-xl font-bold text-foreground">
                    €{(item.price * item.quantity).toFixed(2)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl border border-border p-6 sticky top-32" data-testid="order-summary">
              <h2 className="text-xl font-bold font-serif mb-6">Riepilogo Ordine</h2>

              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-muted-foreground">
                  <span>{t('subtotal')}</span>
                  <span>€{total.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Spedizione</span>
                  <span>{total >= 50 ? 'Gratis' : '€4.99'}</span>
                </div>
                <div className="border-t border-border pt-4">
                  <div className="flex justify-between font-bold text-lg">
                    <span>{t('total')}</span>
                    <span className="text-primary" data-testid="cart-total">
                      €{(total + (total >= 50 ? 0 : 4.99)).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              <Button
                className="w-full btn-primary"
                onClick={() => navigate('/checkout')}
                data-testid="checkout-btn"
              >
                {t('checkout')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>

              <Link to="/products" className="block mt-4">
                <Button variant="outline" className="w-full">
                  {t('continueShopping')}
                </Button>
              </Link>

              {total < 50 && (
                <p className="text-sm text-muted-foreground text-center mt-4">
                  Aggiungi €{(50 - total).toFixed(2)} per la spedizione gratuita!
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
